/**
 * Contrato do catálogo, do lado do consumidor.
 *
 * Espelha os schemas de `tools/cataloger/src/types.ts`. A duplicação é deliberada:
 * o catálogo chega pela rede, e a PWA precisa revalidá-lo por conta própria antes de
 * aplicar qualquer coisa ao banco local (F7-T06) — confiar no produtor é justamente
 * o que permitiria um arquivo corrompido destruir a biblioteca do usuário.
 *
 * A proteção contra divergência entre os dois schemas é o teste
 * `catalog-schema.test.ts`, que valida o `data/catalog/catalog.json` real com este
 * schema. Se o catalogador mudar o formato, aquele teste quebra.
 */
import { z } from 'zod';

export const SUPPORTED_SCHEMA_VERSION = 1;

const isoDate = z.iso.datetime({ offset: false });
const slug = z.string().min(1);

export const mediaProviderSchema = z.enum(['youtube', 'direct', 'manual', 'unknown']);

export const signMediaSchema = z.object({
  provider: mediaProviderSchema,
  externalId: z.string().min(1).optional(),
  watchUrl: z.url().optional(),
  embedUrl: z.url().optional(),
  authorizedDownloadUrl: z.url().optional(),
  mimeType: z.string().min(1).optional(),
  expectedBytes: z.int().positive().optional(),
  checksum: z.string().min(1).optional(),
});

export const categorySchema = z.object({
  id: z.string().min(1),
  sourceId: z.string().min(1).optional(),
  slug,
  name: z.string().min(1),
  sourceUrl: z.url().optional(),
  order: z.int().nonnegative(),
  declaredCount: z.int().nonnegative().optional(),
  actualCount: z.int().nonnegative(),
  createdAt: isoDate,
  updatedAt: isoDate,
});

export const signSchema = z.object({
  id: z.string().min(1),
  sourceId: z.string().min(1).optional(),
  slug,
  name: z.string().min(1),
  normalizedName: z.string().min(1),
  categoryId: z.string().min(1),
  reference: z.string().optional(),
  sourcePageUrl: z.url().optional(),
  media: signMediaSchema,
  order: z.int().nonnegative(),
  active: z.boolean(),
  createdAt: isoDate,
  updatedAt: isoDate,
});

export const catalogManifestSchema = z.object({
  schemaVersion: z.int().positive(),
  catalogVersion: z.string().min(1),
  generatedAt: isoDate,
  sourceUrl: z.url(),
  categoryCount: z.int().nonnegative(),
  signCount: z.int().nonnegative(),
  contentHash: z.string().length(64),
});

export const catalogSchema = z.object({
  schemaVersion: z.int().positive(),
  catalogVersion: z.string().min(1),
  generatedAt: isoDate,
  sourceUrl: z.url(),
  contentHash: z.string().length(64),
  categories: z.array(categorySchema),
  signs: z.array(signSchema),
});

export type MediaProvider = z.infer<typeof mediaProviderSchema>;
export type SignMedia = z.infer<typeof signMediaSchema>;
export type Category = z.infer<typeof categorySchema>;
export type Sign = z.infer<typeof signSchema>;
export type CatalogManifest = z.infer<typeof catalogManifestSchema>;
export type Catalog = z.infer<typeof catalogSchema>;

/** Status de disponibilidade offline de um sinal (PLAN §5, §18). */
export type OfflineStatus = 'none' | 'queued' | 'downloading' | 'available' | 'error';

export interface LocalSignOffline {
  status: OfflineStatus;
  bytesDownloaded?: number;
  totalBytes?: number;
  /** Chave do arquivo no OfflineFileStore. Não é o nome visível do sinal. */
  localFileName?: string;
  checksum?: string;
  /** Provedor que originou o arquivo local — distingue importação manual. */
  sourceProvider?: MediaProvider;
  downloadedAt?: string;
  error?: string;

  /**
   * Referência de mídia vigente quando o arquivo local foi gravado.
   *
   * Comparada com o catálogo a cada sincronização: se divergir, o arquivo local
   * ficou velho. Guardar isso é o que permite detectar a troca sem apagar nada.
   */
  sourceExternalId?: string;

  /**
   * O vídeo mudou na fonte e o arquivo local está desatualizado (PLAN §40).
   *
   * O arquivo antigo **continua tocando** — só é substituído depois que o novo for
   * baixado e validado. Apagar aqui deixaria o usuário sem nada até rebaixar.
   */
  outdated?: boolean;
}

/**
 * Estado pessoal do usuário sobre um sinal.
 *
 * Vive numa tabela separada de `signs` de propósito: a sincronização do catálogo
 * substitui `signs` inteiro, e este registro precisa sobreviver a isso (F6-T12).
 */
export interface LocalSignState {
  signId: string;

  favorite: boolean;
  learned: boolean;

  studyScore: number;
  correctCount: number;
  incorrectCount: number;

  lastViewedAt?: string;
  lastStudiedAt?: string;

  offline: LocalSignOffline;
}

export function emptyLocalSignState(signId: string): LocalSignState {
  return {
    signId,
    favorite: false,
    learned: false,
    studyScore: 0,
    correctCount: 0,
    incorrectCount: 0,
    offline: { status: 'none' },
  };
}
